/**
 * Shared utilities for liming soil data components.
 * Used by both LimeSoilSummary and LimeSoilDataDialog.
 */

import LimeProgramLabInputRow from "../../controllers/LimeProgramLabInputRow"
import LimeProgramLabInputs from "../../controllers/LimeProgramLabInputs"
import LabInputUnit from "../../controllers/LabInputUnit"
import SampleDepth from "../../controllers/SampleDepth"
import NullableMeasureValue from "../../controllers/NullableMeasureValue"
import MeasurementUnit from "../../controllers/MeasurementUnit"
import Geometry from "ol/geom/Geometry"
import {SamplePoint} from "../../components/GeoMap"
import {ELEMENT_SPECIFIC_GRAVITY} from "./staticdata"
import {ColumnMatcher, matchArray} from "../program/CSVParse"
import {customUnits, unitCustom} from "../program/CustomUnits"
import {pointInBoundary} from "../../controllers/boundaryGeo"

/**
 * Match a single row to a land block using spatial matching (point-in-boundary).
 * Uses the parsed OpenLayers geometry, so a sample inside ANY part of a multipart
 * boundary is matched. Returns the matched landId, or null if no coordinates or the
 * point falls outside all blocks. If multiple blocks match, returns the first.
 */
export function matchRowToLand(
    row: LimeProgramLabInputRow,
    geometryMap: Record<number, Geometry>
): number | null {
    if (row.latitude == null || row.longitude == null) return null
    for (const [id, geometry] of Object.entries(geometryMap)) {
        if (pointInBoundary(geometry, row.latitude, row.longitude)) {
            return parseInt(id)
        }
    }
    return null
}

/**
 * Match all lab input rows to land blocks.
 * Sets landId from spatial matching and landName to the matched block name (for display).
 */
export function matchAllRows(
    inputs: LimeProgramLabInputs,
    geometryMap: Record<number, Geometry>,
    landNames?: Map<number, string>
): LimeProgramLabInputs {
    return {
        rows: inputs.rows.map(row => {
            const matchedId = matchRowToLand(row, geometryMap)
            const name = (matchedId != null && landNames) ? (landNames.get(matchedId) ?? '') : ''
            if (row.landId === matchedId && row.landName === name) return row
            return {...row, landId: matchedId, landName: name}
        })
    }
}

// Row with internal ID for tracking in local state
export interface RowWithId extends LimeProgramLabInputRow {
    _id: number
}

// Element types that use specific gravity conversion
export type ConvertibleElement = 'Ca' | 'Mg' | 'K' | 'Na'

/**
 * Create a NullableMeasureValue from a number.
 * mg/kg and ppm are 1:1 equivalent, so metric and imperial are the same.
 */
export function toMeasureValue(value: number): NullableMeasureValue {
    return {metric: value, imperial: value}
}

/**
 * Create a NullableMeasureValue that keeps a null (unmeasured) value null instead of
 * coercing it to 0. Use for optional analyses (P Bray I/II/Olsen/Mehlich III, micros) so an
 * absent lab column stays blank end-to-end rather than reading as a real 0 measurement.
 * mg/kg and ppm are 1:1 equivalent, so metric and imperial are the same.
 */
export function toMeasureValueNullable(value: number | null): NullableMeasureValue {
    return {metric: value, imperial: value}
}

/**
 * Effective exchangeable acidity for the liming calculation. All three lab variants
 * (KCl, Mehlich III, K2SO4) feed the same logic; prefer KCl, then Mehlich III, then K2SO4,
 * treating a total absence as 0. Single source of truth for the fallback order.
 */
export function resolveExchAcid(
    row: Pick<LimeProgramLabInputRow, 'exchH' | 'exchAcidM3' | 'exchAcidK2SO4'>
): number {
    return row.exchH ?? row.exchAcidM3 ?? row.exchAcidK2SO4 ?? 0
}

/**
 * Effective pH for the liming calculation. A sample carries either pH (H2O) or pH (KCl) — never
 * both — so the calc runs on whichever column has a value (a 0 pH means "not provided", since real
 * soil pH is never 0). Prefers H2O when present, else KCl. Single source of truth.
 */
export function resolvePh(row: Pick<LimeProgramLabInputRow, 'phH2O' | 'phKcl'>): number {
    return row.phH2O || (row.phKcl ?? 0)
}

/**
 * Convert meq/100g values to mg/kg by multiplying by specific gravity.
 * Some regions (e.g., Uruguay) report K, Na, Ca, Mg in meq/100g instead of mg/kg.
 */
export function convertMeq100gToMgKg(value: number, element: ConvertibleElement): number {
    return value * ELEMENT_SPECIFIC_GRAVITY[element]
}

/**
 * Convert mg/kg back to meq/100g values by dividing by specific gravity.
 * Used for display when showing stored values to meq/100g users.
 */
export function convertMgKgToMeq100g(value: number, element: ConvertibleElement): number {
    return value / ELEMENT_SPECIFIC_GRAVITY[element]
}

/**
 * Check if region uses meq/100g lab input units.
 */
export function isMeq100g(labInputUnit: LabInputUnit | undefined): boolean {
    return labInputUnit === LabInputUnit.Meq100g
}

/**
 * Parse sample depth from CSV string value.
 * Handles various formats: "SubSoil", "sub_soil", "Subsoil", etc.
 */
export function parseSampleDepth(value: string): SampleDepth {
    const normalized = value.toLowerCase().replace(/[\s_()-]/g, '')
    if (normalized === 'subsoil') return SampleDepth.SubSoil
    if (normalized === 'topsoilmicros') return SampleDepth.TopSoilMicros
    return SampleDepth.TopSoil
}

/**
 * Create an empty row with default values.
 */
export function createEmptyRow(id: number): RowWithId {
    return {
        _id: id,
        landId: null,
        landName: '',
        referenceNr: '',
        labNumber: null,
        phH2O: null,
        phKcl: null,
        pBrayI: toMeasureValueNullable(null),
        pBrayII: toMeasureValueNullable(null),
        pOlsen: toMeasureValueNullable(null),
        pMehlichIII: toMeasureValueNullable(null),
        k: toMeasureValue(0),
        na: toMeasureValue(0),
        ca: toMeasureValue(0),
        mg: toMeasureValue(0),
        exchH: null,
        exchAcidK2SO4: null,
        exchAcidM3: null,
        sulfur: toMeasureValue(0),
        density: 1.2,
        fe: toMeasureValueNullable(null),
        mn: toMeasureValueNullable(null),
        zn: toMeasureValueNullable(null),
        cu: toMeasureValueNullable(null),
        caPercent: null,
        mgPercent: null,
        kPercent: null,
        naPercent: null,
        acidSat: null,
        sValue: null,
        cec: null,
        caMgRatio: null,
        caMgKRatio: null,
        mgKRatio: null,
        naKRatio: null,
        bufferPh: null,
        organicMatter: null,
        caCO3: null,
        latitude: null,
        longitude: null,
        sampleDepth: SampleDepth.TopSoil,
        limeTypeOverride: null,
        limeNeedOverride: null,
        magnesiumNeedOverride: null,
        gypsumNeedOverride: null,
        kNeedOverride: null,
        pNeedOverride: null,
        elementalSulphurOverride: null,
        outputs: null
    }
}

/**
 * Convert LimeProgramLabInputs to RowWithId array for local state management.
 */
export function toRowsWithId(data: LimeProgramLabInputs): RowWithId[] {
    return data.rows.map((row, index) => ({...row, _id: index}))
}

/**
 * Convert RowWithId array back to LimeProgramLabInputs for saving.
 */
export function toLabInputs(data: RowWithId[]): LimeProgramLabInputs {
    return {rows: data.map(({_id, ...row}) => row)}
}

/**
 * Convert rows to sample points for the GeoMap component.
 */
export function rowsToSamplePoints(rows: RowWithId[]): SamplePoint[] {
    return rows
        .filter(row => row.latitude !== null && row.longitude !== null)
        .map(row => ({
            latitude: row.latitude!,
            longitude: row.longitude!,
            label: row.referenceNr || row.landName,
            sampleDepth: row.sampleDepth === SampleDepth.SubSoil ? 'subsoil' : 'topsoil'
        }))
}

/**
 * Format a nullable number for display.
 */
export function formatValue(value: number | null): string {
    if (value === null) return '-'
    return value.toFixed(2)
}

/**
 * Format a NullableMeasureValue for display.
 */
export function formatMeasure(system: MeasurementUnit, value: NullableMeasureValue): string {
    const v = system === MeasurementUnit.METRIC ? value.metric : value.imperial
    if (v === null || v === undefined) return '-'
    return v.toFixed(2)
}

/**
 * Format a nutrient level value in mg/kg (whole number).
 */
export function formatMgKg(value: number | undefined): string {
    if (value === undefined || value === 0) return '-'
    return value.toFixed(0)
}

/**
 * Format a saturation percentage (stored as 0-1 ratio, displayed as %).
 */
export function formatSatPercent(value: number | undefined): string {
    if (value === undefined) return '-'
    return (value * 100).toFixed(1)
}

/**
 * Format K, Na, Ca, Mg values - converts back to meq/100g for display if needed.
 */
export function formatMeasureElement(
    system: MeasurementUnit,
    value: NullableMeasureValue,
    element: ConvertibleElement,
    meq100gMode: boolean
): string {
    const v = system === MeasurementUnit.METRIC ? value.metric : value.imperial
    if (v === null || v === undefined) return '-'
    const displayValue = meq100gMode ? convertMgKgToMeq100g(v, element) : v
    return displayValue.toFixed(2)
}

/**
 * A single soil-analysis column for the read-only display tables.
 * `value` renders the formatted cell text; `sort` is the row field key used by sortable grids
 * (e.g. the playfield) and is omitted for non-sortable columns.
 */
export interface SoilDisplayColumn {
    header: string
    value: (row: LimeProgramLabInputRow) => string
    sort?: string
}

/**
 * The canonical soil-analysis columns (ITEST Soil Export / Online Liming order), shared by every
 * read-only lab-results table so the displayed field set can't drift between them. Covers the
 * analyte columns only; each table supplies its own leading metadata (block, reference, lab number,
 * lat/long, sample depth) and trailing actions.
 */
export function buildSoilDisplayColumns(system: MeasurementUnit, meq100gMode: boolean): SoilDisplayColumn[] {
    // K/Ca/Mg/Na can be meq/100g in some regions; micro-elements are always mg/kg (metric) or ppm (imperial).
    const unitLabel = meq100gMode ? 'meq/100g' : unitCustom(system, customUnits.mgKg_ppm)
    const microUnit = unitCustom(system, customUnits.mgKg_ppm)
    return [
        {header: '(Ca+Mg)/K', value: r => formatValue(r.caMgKRatio), sort: 'caMgKRatio'},
        {header: 'Ca%', value: r => formatValue(r.caPercent), sort: 'caPercent'},
        {header: 'Ca:Mg', value: r => formatValue(r.caMgRatio), sort: 'caMgRatio'},
        {header: 'Density (g/cm3)', value: r => formatValue(r.density), sort: 'density'},
        {header: 'K%', value: r => formatValue(r.kPercent), sort: 'kPercent'},
        {header: 'Mg%', value: r => formatValue(r.mgPercent), sort: 'mgPercent'},
        {header: 'Mg:K', value: r => formatValue(r.mgKRatio), sort: 'mgKRatio'},
        {header: 'Na%', value: r => formatValue(r.naPercent), sort: 'naPercent'},
        {header: 'Na:K', value: r => formatValue(r.naKRatio), sort: 'naKRatio'},
        {header: 'P Bray I', value: r => formatMeasure(system, r.pBrayI), sort: 'pBrayI'},
        {header: 'P Bray II', value: r => formatMeasure(system, r.pBrayII), sort: 'pBrayII'},
        {header: 'P Olsen', value: r => formatMeasure(system, r.pOlsen), sort: 'pOlsen'},
        {header: 'P Mehlich III', value: r => formatMeasure(system, r.pMehlichIII), sort: 'pMehlichIII'},
        {header: 'Sulphur', value: r => formatMeasure(system, r.sulfur), sort: 'sulfur'},
        {header: 'pH (H2O)', value: r => formatValue(r.phH2O), sort: 'phH2O'},
        {header: 'pH (KCl)', value: r => formatValue(r.phKcl), sort: 'phKcl'},
        {header: `K (${unitLabel})`, value: r => formatMeasureElement(system, r.k, 'K', meq100gMode), sort: 'k'},
        {header: `Ca (${unitLabel})`, value: r => formatMeasureElement(system, r.ca, 'Ca', meq100gMode), sort: 'ca'},
        {header: `Mg (${unitLabel})`, value: r => formatMeasureElement(system, r.mg, 'Mg', meq100gMode), sort: 'mg'},
        {header: `Na (${unitLabel})`, value: r => formatMeasureElement(system, r.na, 'Na', meq100gMode), sort: 'na'},
        {header: `Fe (${microUnit})`, value: r => formatMeasure(system, r.fe), sort: 'fe'},
        {header: `Mn (${microUnit})`, value: r => formatMeasure(system, r.mn), sort: 'mn'},
        {header: `Zn (${microUnit})`, value: r => formatMeasure(system, r.zn), sort: 'zn'},
        {header: `Cu (${microUnit})`, value: r => formatMeasure(system, r.cu), sort: 'cu'},
        {header: 'Exch Acid KCL', value: r => formatValue(r.exchH), sort: 'exchH'},
        {header: 'Exch Acid K2SO4', value: r => formatValue(r.exchAcidK2SO4), sort: 'exchAcidK2SO4'},
        {header: 'Exch Acid M3', value: r => formatValue(r.exchAcidM3), sort: 'exchAcidM3'},
        {header: 'CEC / T-value', value: r => formatValue(r.cec), sort: 'cec'},
        {header: 'Acid saturation / H%', value: r => formatValue(r.acidSat), sort: 'acidSat'},
        {header: 'S-value', value: r => formatValue(r.sValue), sort: 'sValue'},
        {header: 'Buffer pH', value: r => formatValue(r.bufferPh), sort: 'bufferPh'},
        {header: 'Organic matter %', value: r => formatValue(r.organicMatter), sort: 'organicMatter'},
        {header: 'CaCO3 (%)', value: r => formatValue(r.caCO3), sort: 'caCO3'},
    ]
}

/**
 * Convert a row's element values for editing (mg/kg to meq/100g if needed).
 */
export function convertRowForEditing(row: RowWithId, meq100gMode: boolean): RowWithId {
    if (!meq100gMode) return {...row}

    return {
        ...row,
        k: toMeasureValue(convertMgKgToMeq100g(row.k.metric || 0, 'K')),
        na: toMeasureValue(convertMgKgToMeq100g(row.na.metric || 0, 'Na')),
        ca: toMeasureValue(convertMgKgToMeq100g(row.ca.metric || 0, 'Ca')),
        mg: toMeasureValue(convertMgKgToMeq100g(row.mg.metric || 0, 'Mg'))
    }
}

/**
 * Convert a row's element values for saving (meq/100g to mg/kg if needed).
 */
export function convertRowForSaving(row: RowWithId, meq100gMode: boolean): RowWithId {
    if (!meq100gMode) return row

    return {
        ...row,
        k: toMeasureValue(convertMeq100gToMgKg(row.k.metric || 0, 'K')),
        na: toMeasureValue(convertMeq100gToMgKg(row.na.metric || 0, 'Na')),
        ca: toMeasureValue(convertMeq100gToMgKg(row.ca.metric || 0, 'Ca')),
        mg: toMeasureValue(convertMeq100gToMgKg(row.mg.metric || 0, 'Mg'))
    }
}

// CSV column name aliases for parsing.
// The first entry in each list is the canonical ITEST Soil Export / Online Liming header;
// the rest are legacy/lab-specific spellings kept for backwards compatibility.
export const CSV_COLUMN_ALIASES = {
    referenceNr: ['Sample Number', 'Reference Nr', 'ReferenceNr', 'Ref', 'Sample#', 'Reference nr.', 'Reference', 'SampleID'],
    labNumber: ['Lab number', 'Lab Number', 'LabNumber', 'LAB No'],
    phH2O: ['pH (H2O)', 'pH(H2O)', 'pH_H2O', 'ph'],
    phKcl: ['pH (KCl)', 'pH(KCl)', 'pH KCl', 'pH (KCL)', 'phKcl', 'pH_KCl'],
    pBrayI: ['P Bray I', 'P_BrayI', 'P', 'PBray', 'P Bray', 'p_bray1'],
    pBrayII: ['P Bray II', 'P Bray 2', 'PBrayII', 'P_BrayII', 'p_bray2'],
    pOlsen: ['P Olsen', 'POlsen', 'P_Olsen'],
    pMehlichIII: ['P Mehlich III', 'P Mehlich 3', 'PMehlichIII', 'Mehlich III P'],
    k: ['K (ppm)', 'K', 'K mg/kg', 'K (mg/kg)', 'K (meq/100g)'],
    na: ['Na (ppm)', 'Na', 'Na mg/kg', 'Na (mg/kg)', 'Na (meq/100g)'],
    ca: ['Ca (ppm)', 'Ca', 'Ca mg/kg', 'Ca (mg/kg)', 'Ca (meq/100g)'],
    mg: ['Mg (ppm)', 'Mg', 'Mg mg/kg', 'Mg (mg/kg)', 'Mg (meq/100g)'],
    fe: ['Fe (ppm)', 'Fe', 'Fe (mg/kg)', 'Fe mg/kg', 'Fe DTPA'],
    mn: ['Mn (ppm)', 'Mn', 'Mn (mg/kg)', 'Mn mg/kg', 'Mn DTPA'],
    zn: ['Zn (ppm)', 'Zn', 'Zn (mg/kg)', 'Zn mg/kg', 'Zn DTPA'],
    cu: ['Cu (ppm)', 'Cu', 'Cu (mg/kg)', 'Cu mg/kg', 'Cu DTPA'],
    exchH: ['Exch Acid KCL', 'Exch H', 'Exch H+', 'ExchH', 'H', 'OUT H+ (cmol(+)/kg)', 'us'],
    exchAcidK2SO4: ['Exch Acid K2SO4', 'ExchAcidK2SO4', 'EXCH ACID K2SO4'],
    exchAcidM3: ['Exch Acid M3', 'ExchAcidM3', 'EXCH ACID M3'],
    sulfur: ['Sulphur', 'S', 'Sulfur', 'S (mg/kg)', 'S (ppm)', 'S Am AC', 'swael'],
    density: ['Density (g/cm3)', 'Density', 'BD', 'digtheid'],
    caPercent: ['Ca%', '%Ca', 'Ca (%)', 'ca_perc'],
    mgPercent: ['Mg%', '%Mg', 'Mg (%)', 'mg_perc'],
    kPercent: ['K%', '%K', 'K (%)', 'k_perc'],
    naPercent: ['Na%', '%Na', 'Na (%)', 'na_perc'],
    acidSat: ['Acid saturation / H%', 'Acid Sat', 'Acid Sat.', 'AcidSat', 'SOUR.V (%)', 'Acid Sat AmAc', 'suur_ver'],
    sValue: ['S-value', 'S-Value', 'SValue', 'S-Value (cmol(+)/kg)', 's_waarde'],
    cec: ['CEC / T-value', 'CEC', 'T', 'T (cmol(+)/kg)', 'T-Value AmAc', 'kuk'],
    caMgRatio: ['Ca:Mg', 'CaMgRatio', 'Ca:Mg (1.5-4.5)', 'ca_mg'],
    caMgKRatio: ['(Ca+Mg)/K', 'CaMgKRatio', '(Ca+Mg)/K (10.0-20.0)', '(Ca+Mg) / K', 'ca_mg_k'],
    mgKRatio: ['Mg:K', 'MgKRatio', 'Mg:K (3.0-4.0)', 'mg_k'],
    naKRatio: ['Na:K', 'NaKRatio', 'nak'],
    bufferPh: ['Buffer pH', 'BufferPh', 'BUFFER PH', 'pH Buffer'],
    organicMatter: ['Organic matter %', 'Organic matter', 'OrganicMatter', 'OM', 'OM (%)'],
    caCO3: ['Calcium carbonate / CaCO3 (%)', 'CaCO3', 'CaCO3 (%)', 'Calcium carbonate'],
    latitude: ['Latitude', 'Lat', 'Y'],
    longitude: ['Longitude', 'Lon', 'Long', 'X'],
    sampleDepth: ['Sample depth', 'Sample Depth', 'SampleDepth', 'Depth'],
    limeTypeOverride: ['Lime Type Override', 'LimeTypeOverride'],
    limeNeedOverride: ['Lime Need Override', 'LimeNeedOverride'],
    magnesiumNeedOverride: ['Magnesium Need Override', 'MagnesiumNeedOverride', 'Mg Need Override'],
    gypsumNeedOverride: ['Gypsum Need Override', 'GypsumNeedOverride'],
    kNeedOverride: ['K Need Override', 'KNeedOverride'],
    pNeedOverride: ['P Need Override', 'PNeedOverride'],
    elementalSulphurOverride: ['Elemental Sulphur Override', 'ElementalSulphurOverride', 'Elem S Override']
} as const

/**
 * Get a value from a CSV row using column aliases.
 */
export function getCsvValue(row: Record<string, string>, aliases: readonly string[]): string {
    for (const alias of aliases) {
        const value = row[alias]
        if (value !== undefined && value !== '') {
            return value
        }
    }
    return ''
}
/**
 * Build column matchers for CSV parsing using parseCSVFile utility.
 * @param meq100gMode - Whether to convert K, Na, Ca, Mg from meq/100g to mg/kg
 */
export function buildLimeSoilColumnMatchers(meq100gMode: boolean): ColumnMatcher<RowWithId>[] {
    // Use isNaN (not `||`) so a real 0 is preserved rather than treated as missing.
    const parseNum = (v: string) => { const n = parseFloat(v); return isNaN(n) ? 0 : n }
    const parseNumNullable = (v: string) => { const n = parseFloat(v); return isNaN(n) ? null : n }
    const parseNumDefault = (v: string, def: number) => { const n = parseFloat(v); return isNaN(n) ? def : n }

    return [
        {match: matchArray(...CSV_COLUMN_ALIASES.referenceNr), populate: (v, e) => e.referenceNr = v},
        {match: matchArray(...CSV_COLUMN_ALIASES.labNumber), populate: (v, e) => e.labNumber = v || null},
        {match: matchArray(...CSV_COLUMN_ALIASES.phH2O), populate: (v, e) => e.phH2O = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.phKcl), populate: (v, e) => e.phKcl = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.pBrayI), populate: (v, e) => e.pBrayI = toMeasureValueNullable(parseNumNullable(v))},
        {match: matchArray(...CSV_COLUMN_ALIASES.pBrayII), populate: (v, e) => e.pBrayII = toMeasureValueNullable(parseNumNullable(v))},
        {match: matchArray(...CSV_COLUMN_ALIASES.pOlsen), populate: (v, e) => e.pOlsen = toMeasureValueNullable(parseNumNullable(v))},
        {match: matchArray(...CSV_COLUMN_ALIASES.pMehlichIII), populate: (v, e) => e.pMehlichIII = toMeasureValueNullable(parseNumNullable(v))},
        {match: matchArray(...CSV_COLUMN_ALIASES.k), populate: (v, e) => {
            const val = parseNum(v)
            e.k = toMeasureValue(meq100gMode ? convertMeq100gToMgKg(val, 'K') : val)
        }},
        {match: matchArray(...CSV_COLUMN_ALIASES.na), populate: (v, e) => {
            const val = parseNum(v)
            e.na = toMeasureValue(meq100gMode ? convertMeq100gToMgKg(val, 'Na') : val)
        }},
        {match: matchArray(...CSV_COLUMN_ALIASES.ca), populate: (v, e) => {
            const val = parseNum(v)
            e.ca = toMeasureValue(meq100gMode ? convertMeq100gToMgKg(val, 'Ca') : val)
        }},
        {match: matchArray(...CSV_COLUMN_ALIASES.mg), populate: (v, e) => {
            const val = parseNum(v)
            e.mg = toMeasureValue(meq100gMode ? convertMeq100gToMgKg(val, 'Mg') : val)
        }},
        {match: matchArray(...CSV_COLUMN_ALIASES.fe), populate: (v, e) => e.fe = toMeasureValueNullable(parseNumNullable(v))},
        {match: matchArray(...CSV_COLUMN_ALIASES.mn), populate: (v, e) => e.mn = toMeasureValueNullable(parseNumNullable(v))},
        {match: matchArray(...CSV_COLUMN_ALIASES.zn), populate: (v, e) => e.zn = toMeasureValueNullable(parseNumNullable(v))},
        {match: matchArray(...CSV_COLUMN_ALIASES.cu), populate: (v, e) => e.cu = toMeasureValueNullable(parseNumNullable(v))},
        // ExchH is the KCl exch-acidity and stays null when the lab reports no KCl value (e.g. Waters).
        // K2SO4 and Mehlich III are stored raw; the liming calculation resolves the effective value
        // (KCl -> Mehlich III -> K2SO4) so all three feed the same calculation logic.
        {match: matchArray(...CSV_COLUMN_ALIASES.exchH), populate: (v, e) => e.exchH = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.exchAcidK2SO4), populate: (v, e) => e.exchAcidK2SO4 = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.exchAcidM3), populate: (v, e) => e.exchAcidM3 = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.sulfur), populate: (v, e) => e.sulfur = toMeasureValue(parseNum(v))},
        {match: matchArray(...CSV_COLUMN_ALIASES.density), populate: (v, e) => e.density = parseNumDefault(v, 1.2)},
        {match: matchArray(...CSV_COLUMN_ALIASES.caPercent), populate: (v, e) => e.caPercent = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.mgPercent), populate: (v, e) => e.mgPercent = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.kPercent), populate: (v, e) => e.kPercent = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.naPercent), populate: (v, e) => e.naPercent = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.acidSat), populate: (v, e) => e.acidSat = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.sValue), populate: (v, e) => e.sValue = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.cec), populate: (v, e) => e.cec = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.caMgRatio), populate: (v, e) => e.caMgRatio = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.caMgKRatio), populate: (v, e) => e.caMgKRatio = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.mgKRatio), populate: (v, e) => e.mgKRatio = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.naKRatio), populate: (v, e) => e.naKRatio = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.bufferPh), populate: (v, e) => e.bufferPh = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.organicMatter), populate: (v, e) => e.organicMatter = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.caCO3), populate: (v, e) => e.caCO3 = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.latitude), populate: (v, e) => e.latitude = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.longitude), populate: (v, e) => e.longitude = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.sampleDepth), populate: (v, e) => e.sampleDepth = parseSampleDepth(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.limeTypeOverride), populate: (v, e) => e.limeTypeOverride = v || null},
        {match: matchArray(...CSV_COLUMN_ALIASES.limeNeedOverride), populate: (v, e) => e.limeNeedOverride = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.magnesiumNeedOverride), populate: (v, e) => e.magnesiumNeedOverride = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.gypsumNeedOverride), populate: (v, e) => e.gypsumNeedOverride = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.kNeedOverride), populate: (v, e) => e.kNeedOverride = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.pNeedOverride), populate: (v, e) => e.pNeedOverride = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.elementalSulphurOverride), populate: (v, e) => e.elementalSulphurOverride = parseNumNullable(v)},
    ]
}
